import {useEffect, useReducer, useState} from 'react';

import {AfriexFeatureCode} from '@afriex/core/domain/enums';

import {useFeatureActive} from '@/hooks/useFeatureActive';
import {merchantService} from '@/services/merchants';
import type {AfriexAdminAmount} from '@/types';
import {AfriexCurrencyCode} from '@/types';
import type {MarketPlaceAsk} from '@/types/MarketPlace';
import {getErrorMessage} from '@/utils/errorMessage';

import {isExpired, isFutureDate} from '../utils/date';
import {toAdminAmount} from '../utils/format';
import {buildCreatePayload, buildUpdatePayload} from '../utils/payload';

export interface AskFormState {
  volume: AfriexAdminAmount;
  quote: AfriexAdminAmount;
  minBidVolume: string;
  maxAllowedOfferPrice: string;
  expiresAt?: Date;
  isLoading: boolean;
  error: string | null;
}

type AskFormAction =
  | {type: 'RESET'; payload?: Partial<AskFormState>}
  | {type: 'SET_VOLUME'; payload: AfriexAdminAmount}
  | {type: 'SET_QUOTE'; payload: AfriexAdminAmount}
  | {type: 'SET_MIN_BID_VOLUME'; payload: string}
  | {type: 'SET_MAX_ALLOWED_OFFER_PRICE'; payload: string}
  | {type: 'SET_EXPIRES_AT'; payload?: Date}
  | {type: 'SET_LOADING'; payload: boolean}
  | {type: 'SET_ERROR'; payload: string | null};

const initialState: AskFormState = {
  volume: {currency: AfriexCurrencyCode.USD} as AfriexAdminAmount,
  quote: {} as AfriexAdminAmount,
  minBidVolume: '',
  maxAllowedOfferPrice: '',
  expiresAt: undefined,
  isLoading: false,
  error: null,
};

const reducer = (state: AskFormState, action: AskFormAction): AskFormState => {
  switch (action.type) {
    case 'RESET':
      return {...initialState, ...action.payload};
    case 'SET_VOLUME':
      return {...state, volume: action.payload};
    case 'SET_QUOTE':
      return {...state, quote: action.payload};
    case 'SET_MIN_BID_VOLUME':
      return {...state, minBidVolume: action.payload};
    case 'SET_MAX_ALLOWED_OFFER_PRICE':
      return {...state, maxAllowedOfferPrice: action.payload};
    case 'SET_EXPIRES_AT':
      return {...state, expiresAt: action.payload};
    case 'SET_LOADING':
      return {...state, isLoading: action.payload};
    case 'SET_ERROR':
      return {...state, error: action.payload};
    default:
      return state;
  }
};

interface UseAskModalOptions {
  mode: 'create' | 'edit';
  isOpen: boolean;
  ask?: MarketPlaceAsk | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const useAskModal = ({
  mode,
  isOpen,
  ask,
  onClose,
  onSuccess,
}: UseAskModalOptions) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  // The volume-currency list only exists while the multi-channel rollout is
  // on. Off (or not loaded), `allowedVolumeCurrencies` stays undefined and the
  // AmountInput keeps every currency, which is the legacy admin behaviour.
  const {isActive: isMultiChannelEnabled, hasLoaded: hasLoadedFeature} =
    useFeatureActive(AfriexFeatureCode.MARKET_PLACE_BIDIRECTIONAL);
  const [fetchedVolumeCurrencies, setFetchedVolumeCurrencies] =
    useState<AfriexCurrencyCode[]>();

  useEffect(() => {
    if (!isOpen || !hasLoadedFeature || !isMultiChannelEnabled) {
      return;
    }
    let active = true;
    merchantService
      .getMarketPlaceChannels()
      .then((channels) => {
        if (!active) {
          return;
        }
        const currencies = Object.keys(channels) as AfriexCurrencyCode[];
        setFetchedVolumeCurrencies(currencies);
        // A new ask opens on the default volume currency; when that one has no
        // payable rail, start on the first currency that does instead of
        // leaving a selection the server will reject.
        if (
          mode === 'create' &&
          currencies.length &&
          !currencies.includes(initialState.volume.currency)
        ) {
          dispatch({
            type: 'SET_VOLUME',
            payload: {currency: currencies[0]} as AfriexAdminAmount,
          });
        }
      })
      .catch(() => {
        if (active) {
          // A failed lookup is not "no currency is payable": fall back to the
          // legacy unfiltered dropdown rather than blocking ask creation.
          setFetchedVolumeCurrencies(undefined);
        }
      });
    return () => {
      active = false;
    };
  }, [isOpen, hasLoadedFeature, isMultiChannelEnabled, mode]);

  const allowedVolumeCurrencies =
    hasLoadedFeature && isMultiChannelEnabled
      ? fetchedVolumeCurrencies
      : undefined;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (mode === 'edit' && ask) {
      dispatch({
        type: 'RESET',
        payload: {
          volume: toAdminAmount(ask.volume),
          quote: toAdminAmount(ask.quote),
          minBidVolume: ask.meta?.minBidVolume ?? '',
          maxAllowedOfferPrice: ask.meta?.maxAllowedOfferPrice ?? '',
          expiresAt:
            ask.expiresAt && !isExpired(ask.expiresAt)
              ? new Date(ask.expiresAt)
              : undefined,
          isLoading: false,
          error: null,
        },
      });
    } else if (mode === 'create') {
      dispatch({type: 'RESET'});
    }
  }, [mode, isOpen, ask]);

  const handleClose = () => {
    dispatch({type: 'RESET'});
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch({type: 'SET_ERROR', payload: null});

    if (
      !state.volume?.currency ||
      !state.volume?.amount ||
      !state.quote?.currency ||
      !state.quote?.amount
    ) {
      dispatch({type: 'SET_ERROR', payload: 'All fields are required'});
      return;
    }

    if (state.expiresAt && !isFutureDate(state.expiresAt)) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Expiration date must be in the future',
      });
      return;
    }

    dispatch({type: 'SET_LOADING', payload: true});
    try {
      if (mode === 'create') {
        await merchantService.createMarketPlaceAsk(buildCreatePayload(state));
      } else if (mode === 'edit' && ask) {
        await merchantService.updateMarketPlaceAsk(
          ask.id,
          buildUpdatePayload(state),
        );
      }
      dispatch({type: 'RESET'});
      onSuccess();
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        payload: getErrorMessage(err, `Failed to ${mode} offer`),
      });
    } finally {
      dispatch({type: 'SET_LOADING', payload: false});
    }
  };

  const isDisabled =
    !state.volume?.currency ||
    !state.volume?.amount ||
    !state.quote?.currency ||
    !state.quote?.amount ||
    state.isLoading;

  return {
    state,
    dispatch,
    handleClose,
    handleSubmit,
    isDisabled,
    allowedVolumeCurrencies,
    title: mode === 'create' ? 'Create Offer' : 'Edit Offer',
    submitLabel: mode === 'create' ? 'Submit' : 'Save',
  };
};
